import { DatabaseZap, CheckCircle2, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { Badge, Button } from '../../ui';
import { useGoalKbProvisioning } from '../../features/goals/useGoalKbProvisioning';

/**
 * Goal → Knowledge Base (provisioning) area.
 *
 * On load it idempotently starts provisioning the SEPARATE Goal knowledge base
 * (sectionKey "goal"). If the KB already exists it shows "ready" immediately;
 * otherwise it polls DescribeExecution every 5s, showing a spinner + a friendly
 * per-step label while the workflow runs, until it reaches ready (SUCCEEDED) or
 * error (with a Retry button that re-starts provisioning). A "Provision Goal
 * Knowledge Base" button is offered before/while idle for manual control.
 *
 * Presentational only — all provisioning logic lives in
 * {@link useGoalKbProvisioning}. Matches the shared Tailwind dark UI + the
 * sibling {@link GoalKnowledgePanel} styling.
 */
export function GoalKnowledgeBaseStatus() {
  const { state, step, error, alreadyProvisioned, start } =
    useGoalKbProvisioning();

  const busy = state === 'starting' || state === 'running';
  const ready = state === 'ready';

  return (
    <section
      aria-label="Goal knowledge base"
      className="space-y-2 rounded-xl border border-surface-border bg-surface-1/40 px-3.5 py-3"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          <DatabaseZap className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
          Knowledge base
        </p>
        {ready && (
          <Badge tone="success" dot>
            Ready
          </Badge>
        )}
      </div>

      {/* READY — provisioned (either already, or just finished) */}
      {ready && (
        <p
          className="flex items-center gap-2 text-sm text-emerald-300"
          role="status"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Goal knowledge base is ready
            {alreadyProvisioned ? '.' : ' — provisioning complete.'}
          </span>
        </p>
      )}

      {/* BUSY — starting or polling the provisioning workflow */}
      {busy && (
        <div
          className="flex items-center gap-2 text-sm text-zinc-400"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          <span>
            {state === 'starting'
              ? 'Starting Goal knowledge base provisioning…'
              : step || 'Provisioning knowledge base…'}
          </span>
        </div>
      )}

      {/* ERROR — with Retry */}
      {state === 'error' && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-medium">Provisioning failed</p>
              <p className="mt-0.5 text-xs text-red-300/80">
                {error ?? 'The Goal knowledge base could not be provisioned.'}
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void start()}
            leftIcon={<RotateCcw className="h-4 w-4" />}
          >
            Retry
          </Button>
        </div>
      )}

      {/* IDLE — manual provision (fallback when auto-start is not desired) */}
      {state === 'idle' && (
        <div className="space-y-1.5">
          <p className="text-sm text-zinc-400">
            Provision the separate Goal knowledge base to upload documents and
            ask grounded questions.
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void start()}
            leftIcon={<DatabaseZap className="h-4 w-4" />}
          >
            Provision Goal Knowledge Base
          </Button>
        </div>
      )}
    </section>
  );
}

export default GoalKnowledgeBaseStatus;
